import { alamatUlasanPendek } from '../utils/ulasanPendek';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Divider, FormControl, FormControlLabel, FormLabel, Grid,
  InputLabel, MenuItem, Radio, RadioGroup, Select, Switch, TextField, Typography,
} from '@mui/material';
import { Print as PrintIcon, Save as SaveIcon } from '@mui/icons-material';
import axios from 'axios';
import { apiHost } from '../utils/apiHost';
import { BAGIAN_STRUK, KOLOM_58MM, KOLOM_80MM, buatBarisStruk, urutanLengkap } from '../struk/strukLayout';
import { SusunanStruk } from './StrukSusunan';
import { useNavigate } from 'react-router-dom';
import StrukPratinjau from '../struk/StrukPratinjau';
import { cetakBrowser } from '../struk/cetakStruk';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const api = axios.create({ baseURL: getApiBaseUrl(), withCredentials: true });

const MAKS_CATATAN_KAKI = 255;
const MAKS_BARIS = 3;

// Contoh transaksi untuk pratinjau (tidak disimpan).
const BAHAN_CONTOH = {
  waktu: '17/10/2026 14:05',
  orderId: 'ORDER-1760000000000',
  kasir: 'Rina',
  customer: 'Budi',
  meja: 'A3',
  nomorAntrian: '012',
  noted: 'tanpa sambal',
  items: [{ nama: 'Mie SP 2', harga: 15000, qty: 2, catatan: 'pedas' }, { nama: 'Es Teh', harga: 5000, qty: 1 }],
};
const CONTOH = {
  tunai: { nama: 'Tunai', trx: { ...BAHAN_CONTOH, metodeBayar: 'Cash', total: 35000, uangCustomer: 50000 } },
  promo: {
    nama: 'Promo + PPN',
    trx: { ...BAHAN_CONTOH, metodeBayar: 'QRIS', subtotal: 31500, diskonPromo: 3500, namaPromo: 'Weekend', ppnPersen: 11, ppnNominal: 3465, total: 34965 },
  },
  bagi: { nama: 'Bayar bagi', trx: { ...BAHAN_CONTOH, metodeBayar: 'Split', total: 35000, barisBagi: ['Tunai: Rp 20.000', 'BCA: Rp 15.000'] } },
};

const SAKELAR_AWAL = Object.fromEntries(BAGIAN_STRUK.map((k) => [k, true]));

export const StrukPelanggan = ({ cabangUuid, konfigurasi, namaCabang, mutate }) => {
  const [form, setForm] = useState({ wifiNama: '', wifi: '', teksA: '', teksB: '', logoTampilStruk: true, strukLebar: 58, strukCatatanKaki: '' });
  const [tampil, setTampil] = useState(SAKELAR_AWAL);
  const [urutan, setUrutan] = useState(urutanLengkap(null));
  const [contoh, setContoh] = useState('tunai');
  const [menyimpan, setMenyimpan] = useState(false);
  const [umpanBalik, setUmpanBalik] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const k = konfigurasi || {};
    setForm({
      wifiNama: k.wifiNama || '',
      wifi: k.wifi || '',
      teksA: k.teksA || '',
      teksB: k.teksB || '',
      logoTampilStruk: k.logoTampilStruk !== false,
      strukLebar: Number(k.strukLebar) === 80 ? 80 : 58,
      strukCatatanKaki: k.strukCatatanKaki || '',
    });
    setTampil({ ...SAKELAR_AWAL, ...(k.strukTampil && typeof k.strukTampil === 'object' ? k.strukTampil : {}) });
    setUrutan(urutanLengkap(k.strukUrutan));
  }, [konfigurasi]);

  const ubah = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  const jumlahBaris = form.strukCatatanKaki ? form.strukCatatanKaki.split('\n').length : 0;
  const kakiTerlaluPanjang = form.strukCatatanKaki.length > MAKS_CATATAN_KAKI || jumlahBaris > MAKS_BARIS;

  // Tautan ulasan diisi superadmin di menu Ulasan Google; di sini hanya alamat pendek di QR-nya (untuk pratinjau) dan sakelarnya diatur.
  const ulasanUrl = alamatUlasanPendek(konfigurasi);

  const logoUrl = konfigurasi?.logo ? `${getApiBaseUrl()}/uploads/${konfigurasi.logo}` : '';

  // Pratinjau: aturan tata letak SAMA dengan yang dipakai kasir web & aplikasi Flutter (struk/strukLayout.js).
  const baris = useMemo(() => buatBarisStruk({
    cabang: {
      nama: namaCabang || 'Nama Cabang',
      noTelp: konfigurasi?.noTelp || '0812-3456-7890',
      wifiNama: form.wifiNama,
      wifi: form.wifi,
      teksA: form.teksA,
      teksB: form.teksB,
      logoUrl: form.logoTampilStruk ? logoUrl : '',
      ulasanUrl,
    },
    trx: { namaCabang: namaCabang || 'Nama Cabang', ...CONTOH[contoh].trx },
    kolom: form.strukLebar === 80 ? KOLOM_80MM : KOLOM_58MM,
    tampil,
    catatanKaki: form.strukCatatanKaki,
    urutan,
  }), [namaCabang, konfigurasi, form, tampil, urutan, contoh, logoUrl]);

  const simpan = async () => {
    setMenyimpan(true);
    setUmpanBalik(null);
    try {
      await api.put('/updatekonfigurasicabang', {
        cabangUuid,
        wifiNama: form.wifiNama,
        wifi: form.wifi,
        teksA: form.teksA,
        teksB: form.teksB,
        logoTampilStruk: form.logoTampilStruk,
        strukLebar: form.strukLebar,
        strukCatatanKaki: form.strukCatatanKaki,
        strukTampil: tampil,
        strukUrutan: urutan,
      });
      await mutate();
      setUmpanBalik({ type: 'success', msg: 'Pengaturan struk disimpan. Berlaku di kasir web dan aplikasi HP (HP memuat ulang saat berikutnya terhubung).' });
    } catch (err) {
      setUmpanBalik({ type: 'error', msg: err.response?.data?.msg || err.response?.data?.message || 'Gagal menyimpan pengaturan struk.' });
    } finally {
      setMenyimpan(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, alignItems: 'flex-start', maxWidth: 1180 }}>
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <Card sx={{ mb: 2 }}>
              <CardHeader title="Kertas" />
              <CardContent>
                <FormControl>
                  <FormLabel>Lebar kertas printer</FormLabel>
                  <RadioGroup row value={form.strukLebar} onChange={(e) => setForm((p) => ({ ...p, strukLebar: Number(e.target.value) }))}>
                    <FormControlLabel value={58} control={<Radio />} label="58 mm (32 karakter)" />
                    <FormControlLabel value={80} control={<Radio />} label="80 mm (48 karakter)" />
                  </RadioGroup>
                </FormControl>
              </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
              <CardHeader title="Kepala struk" subheader="Nama cabang selalu tampil. Nomor telepon diatur di menu Konfigurasi (juga dipakai halaman menu)." />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Nama WiFi (SSID)" name="wifiNama" value={form.wifiNama} onChange={ubah} disabled={menyimpan} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Password WiFi" name="wifi" value={form.wifi} onChange={ubah} disabled={menyimpan} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Teks A (mis. alamat)" name="teksA" value={form.teksA} onChange={ubah} disabled={menyimpan} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Teks B (mis. jam buka)" name="teksB" value={form.teksB} onChange={ubah} disabled={menyimpan} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <SusunanStruk
              urutan={urutan}
              setUrutan={setUrutan}
              tampil={tampil}
              setTampil={setTampil}
              logoStruk={form.logoTampilStruk}
              setLogoStruk={(v) => setForm((p) => ({ ...p, logoTampilStruk: v }))}
              adaLogo={Boolean(logoUrl)}
              disabled={menyimpan}
            />

            <Card sx={{ mb: 2 }}>
              <CardHeader title="Catatan kaki" subheader='Menggantikan tulisan "Terima Kasih!" di bagian bawah struk. Kosongkan untuk memakai bawaan.' />
              <CardContent>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={3}
                  size="small"
                  label="Catatan kaki"
                  name="strukCatatanKaki"
                  value={form.strukCatatanKaki}
                  onChange={ubah}
                  disabled={menyimpan}
                  error={kakiTerlaluPanjang}
                  helperText={kakiTerlaluPanjang
                    ? `Maksimal ${MAKS_BARIS} baris dan ${MAKS_CATATAN_KAKI} karakter.`
                    : `${form.strukCatatanKaki.length}/${MAKS_CATATAN_KAKI} karakter, ${jumlahBaris}/${MAKS_BARIS} baris`}
                />
              </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
              <CardHeader
                title="Ulasan Google"
                subheader="QR ke halaman tulis ulasan Google di bawah struk. Tautan ulasan cabang diisi di menu Ulasan Google."
              />
              <CardContent>
                <FormControlLabel
                  control={<Switch checked={tampil.ulasan !== false} onChange={(e) => setTampil((p) => ({ ...p, ulasan: e.target.checked }))} disabled={menyimpan} />}
                  label="Cetak QR ulasan di bawah struk"
                />
                {ulasanUrl ? (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-all' }}>
                    Tautan: {ulasanUrl}
                  </Typography>
                ) : (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Tautan ulasan Google belum diisi, jadi QR belum tercetak.
                  </Alert>
                )}
                <Button size="small" variant="outlined" sx={{ mt: 1.5 }} onClick={() => navigate('/ulasangoogle')}>
                  {ulasanUrl ? 'Ubah tautan di menu Ulasan Google' : 'Isi tautan di menu Ulasan Google'}
                </Button>
              </CardContent>
            </Card>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={simpan} disabled={menyimpan || kakiTerlaluPanjang}>
                {menyimpan ? 'Menyimpan...' : 'Simpan'}
              </Button>
              {umpanBalik && <Alert severity={umpanBalik.type} sx={{ flex: 1, minWidth: 240 }}>{umpanBalik.msg}</Alert>}
            </Box>
          </Box>

          <Box sx={{ width: { xs: '100%', lg: 420 }, flexShrink: 0, alignSelf: 'stretch' }}>
            <Box sx={{ position: { lg: 'sticky' }, top: { lg: 16 }, maxHeight: { lg: 'calc(100vh - 170px)' }, overflowY: { lg: 'auto' }, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Pratinjau</Typography>
              <FormControl size="small" sx={{ minWidth: 180, mb: 1.5 }}>
                <InputLabel>Contoh transaksi</InputLabel>
                <Select label="Contoh transaksi" value={contoh} onChange={(e) => setContoh(e.target.value)}>
                  {Object.entries(CONTOH).map(([k, v]) => <MenuItem key={k} value={k}>{v.nama}</MenuItem>)}
                </Select>
              </FormControl>
              <Box sx={{ overflowX: 'auto', pb: 1 }}>
                <StrukPratinjau baris={baris} />
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={() => cetakBrowser(baris)}>
                Cetak contoh
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Pratinjau memakai pengaturan di layar ini dan belum tersimpan sebelum Anda menekan Simpan. Logo dan ukuran huruf di printer asli bisa sedikit berbeda.
              </Typography>
            </Box>
          </Box>
        </Box>
  );
};
